<template>
    <div>
        <ts-page-title
            :title="$t('loanTypeUserDefinedRate.pageTitle')"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                { text: $t('loanTypeUserDefinedRate.pageTitle'), active: true }
            ]"
        />
        <ts-panel>
            <ts-panel-wrapper class="tw-flex tw-items-center tw-justify-between">
                <Button type="info" ghost @click="addNew">
                    {{ $t("addNew") }}
                </Button>
            </ts-panel-wrapper>
            <Table
                :columns="columns"
                :data="resources"
                size="small"
                :loading="loading"
                max-height="700"
                stripe
            >
                <template slot-scope="{ row }" slot="loan_type_id">
                    {{ row.loan_type ? row.loan_type.loan_type_en : "" }}
                </template>
                <template slot-scope="{ row }" slot="interest_rates">
                    <span v-if="row.interest_rates && row.interest_rates.length">
                        <Tag v-for="(v, i) in row.interest_rates" :key="i" color="blue">{{ v }}%</Tag>
                    </span>
                    <span v-else>—</span>
                </template>
                <template slot-scope="{ row }" slot="monthly_fees">
                    <span v-if="row.monthly_fees && row.monthly_fees.length">
                        <Tag v-for="(v, i) in row.monthly_fees" :key="i" color="green">{{ v }}%</Tag>
                    </span>
                    <span v-else>—</span>
                </template>
                <template slot-scope="{ row }" slot="admin_fees">
                    <span v-if="row.admin_fees && row.admin_fees.length">
                        <Tag v-for="(v, i) in row.admin_fees" :key="i" color="gold">{{ v }}%</Tag>
                    </span>
                    <span v-else>—</span>
                </template>
                <template slot-scope="{ row }" slot="insurance_fees">
                    <span v-if="row.insurance_fees && row.insurance_fees.length">
                        <Tag v-for="(v, i) in row.insurance_fees" :key="i" color="cyan">{{ v }}%</Tag>
                    </span>
                    <span v-else>—</span>
                </template>
                <template slot-scope="{ row }" slot="rescue_fees">
                    <span v-if="row.rescue_fees && row.rescue_fees.length">
                        <Tag v-for="(v, i) in row.rescue_fees" :key="i" color="purple">{{ v }}</Tag>
                    </span>
                    <span v-else>—</span>
                </template>
                <template slot-scope="{ row }" slot="is_active">
                    <Badge v-if="row.is_active" status="success" text="Active" />
                    <Badge v-else status="default" text="Inactive" />
                </template>
                <template slot-scope="{ row }" slot="action">
                    <a
                        href="#"
                        class="text-primary ml-2"
                        @click.prevent="onEdit(row)"
                        v-tooltip="$t('edit')"
                    >
                        <Icon type="ios-create" size="20" />
                    </a>
                    <a
                        href="#"
                        class="text-danger ml-2"
                        @click.prevent="onDelete(row)"
                        v-tooltip="$t('delete')"
                    >
                        <Icon type="ios-trash" size="20" />
                    </a>
                </template>
            </Table>
            <div class="tw-flex tw-items-center tw-justify-end tw-p-4">
                <ts-i-pagination v-model="pagination" @navigate="fetchData" />
            </div>
            <!-- form modal -->
            <Modal
                v-model="showForm"
                draggable
                sticky
                scrollable
                :mask="false"
                :footer-hide="true"
                :z-index="1022"
                width="650"
                :title="$t('loanTypeUserDefinedRate.pageTitle')"
            >
                <from-action
                    ref="form_action"
                    @cancel="clearEdit"
                    @fetchData="fetchData"
                />
            </Modal>
        </ts-panel>
    </div>
</template>

<script>
import { mapState } from "vuex";
import FromAction from "./form";

export default {
    name: "loanTypeUserDefinedRateIndex",
    components: { FromAction },
    data() {
        return {
            loading: false,
            showForm: false
        };
    },
    computed: {
        ...mapState("creditOperation/loanTypeUserDefinedRates", [
            "resources",
            "pagination"
        ]),
        columns() {
            return [
                {
                    title: this.$t("loanTypeUserDefinedRate.loanType"),
                    slot: "loan_type_id",
                    minWidth: 140
                },
                {
                    title: this.$t("loanTypeUserDefinedRate.interestRates"),
                    slot: "interest_rates",
                    minWidth: 160
                },
                {
                    title: this.$t("loanTypeUserDefinedRate.monthlyFees"),
                    slot: "monthly_fees",
                    minWidth: 160
                },
                {
                    title: this.$t("loanTypeUserDefinedRate.adminFees"),
                    slot: "admin_fees",
                    minWidth: 160
                },
                {
                    title: this.$t("loanTypeUserDefinedRate.insuranceFees"),
                    slot: "insurance_fees",
                    minWidth: 160
                },
                {
                    title: this.$t("loanTypeUserDefinedRate.rescueFees"),
                    slot: "rescue_fees",
                    minWidth: 160
                },
                {
                    title: this.$t("loanTypeUserDefinedRate.status"),
                    slot: "is_active",
                    align: "center",
                    width: 100
                },
                {
                    title: this.$t("actions"),
                    slot: "action",
                    fixed: "right",
                    align: "center",
                    className: "tw-space-x-2",
                    width: 90
                }
            ];
        }
    },
    methods: {
        fetchData(attributes) {
            this.loading = true;
            this.$store
                .dispatch("creditOperation/loanTypeUserDefinedRates/fetch", {
                    ...attributes
                })
                .then(() => {
                    this.loading = false;
                })
                .catch(error => {
                    this.loading = false;
                    this.notice({ type: "error", text: error.message });
                });
        },
        onEdit(record) {
            this.$store.commit(
                "creditOperation/loanTypeUserDefinedRates/SET_EDIT_DATA",
                record
            );
            this.showForm = true;
            this.$refs.form_action.fetchResource();
        },
        addNew() {
            this.$store.commit(
                "creditOperation/loanTypeUserDefinedRates/SET_EDIT_DATA",
                {}
            );
            this.showForm = true;
            this.$refs.form_action.fetchResource();
            this.$refs.form_action.clearInput();
        },
        clearEdit() {
            this.showForm = false;
            this.$refs.form_action.clearInput();
            this.$store.commit(
                "creditOperation/loanTypeUserDefinedRates/SET_EDIT_DATA",
                {}
            );
        },
        onDelete(record) {
            this.$Modal.confirm({
                title: this.$t("delete"),
                content: this.$t("loanTypeUserDefinedRate.confirmDelete"),
                onOk: () => {
                    this.$store
                        .dispatch(
                            "creditOperation/loanTypeUserDefinedRates/destroy",
                            record.rate_id
                        )
                        .then(response => {
                            this.notice({ type: "success", text: response.message });
                            this.fetchData();
                        })
                        .catch(error => {
                            this.notice({ type: "error", text: error.message });
                        });
                }
            });
        },
        notice(not) {
            this.$Notice[not.type]({
                title: "LOAN TYPE USER DEFINED RATE",
                desc: not.text
            });
        }
    },
    beforeRouteLeave(to, from, next) {
        this.$store.commit(
            "creditOperation/loanTypeUserDefinedRates/RESET_STATE"
        );
        next();
    },
    beforeRouteEnter(to, from, next) {
        next(vm => {
            vm.fetchData();
        });
    }
};
</script>
